/**
 * Quote-cart store — Zustand, persisted to localStorage.
 *
 * NOT a real checkout. Holds line items that get submitted as a contact form
 * to /.netlify/functions/quote-submit, which emails Aiden the quote request.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface QuoteLineItem {
  slug: string;
  sku: string;
  name: string;
  optionId: string;
  optionLabel: string;
  price: number;
  qty: number;
}

interface QuoteState {
  items: QuoteLineItem[];
  add: (item: Omit<QuoteLineItem, 'qty'> & { qty?: number }) => void;
  remove: (slug: string, optionId: string) => void;
  setQty: (slug: string, optionId: string, qty: number) => void;
  clear: () => void;
  count: () => number;
  total: () => number;
}

export const useQuoteStore = create<QuoteState>()(
  persist(
    (set, get) => ({
      items: [],
      add: (incoming) =>
        set((state) => {
          const qty = incoming.qty ?? 1;
          const idx = state.items.findIndex(
            (i) => i.slug === incoming.slug && i.optionId === incoming.optionId,
          );
          if (idx >= 0) {
            const next = state.items.slice();
            next[idx] = { ...next[idx], qty: next[idx].qty + qty };
            return { items: next };
          }
          return { items: [...state.items, { ...incoming, qty }] };
        }),
      remove: (slug, optionId) =>
        set((state) => ({
          items: state.items.filter((i) => !(i.slug === slug && i.optionId === optionId)),
        })),
      setQty: (slug, optionId, qty) =>
        set((state) => ({
          items: state.items
            .map((i) =>
              i.slug === slug && i.optionId === optionId ? { ...i, qty: Math.max(1, qty) } : i,
            )
            .filter((i) => i.qty > 0),
        })),
      clear: () => set({ items: [] }),
      count: () => get().items.reduce((n, i) => n + i.qty, 0),
      total: () => get().items.reduce((n, i) => n + i.price * i.qty, 0),
    }),
    {
      name: 'v9.quote-cart.v1',
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
